import React, { useCallback, useEffect, useState } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/ErrorState';
import EmptyState from '../ui/EmptyState';
import { Spinner } from '../ui/Spinner';
import { formatDateTime } from '../../utils/formatDate';
import { quotesAPI } from '../../quote-builder/quoteAPI';
import api from '../../services/api';
import { deriveAcknowledgmentRows } from './acknowledgmentRows';
import { useTenantUserNames } from '../../services/tenantUserNames';
import useTableSort from '../ui/useTableSort';

// IDI-479: click-to-sort. Expand and Download columns are not sortable; the
// evidence row stays under its parent because each Fragment is keyed per row.
const ACK_SORT_COLUMNS = [
  { key: 'at', type: 'date', value: (r) => r.at || null },
  { key: 'event', type: 'text', value: (r) => r.eventLabel || null },
  { key: 'document', type: 'text', value: (r) => [r.documentLabel || r.documentNumber, r.sendLabel].filter(Boolean).join(' · ') || null },
  { key: 'how', type: 'text', value: (r) => r.howLabel || null },
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * IDI-561: a stored actor as a person's name, never an internal id. A known
 * user id → its IDI-550 name; any other id → "Unknown user"; anything else
 * (an e-sign typed name, a recorder name, an email) is shown as stored.
 */
function resolveActor(actor, namesBySub) {
  const v = typeof actor === 'string' ? actor.trim() : '';
  if (!v) return '';
  const names = namesBySub || {};
  const known = typeof names.get === 'function' ? names.get(v) : names[v];
  if (typeof known === 'string' && known.trim() && !UUID_RE.test(known.trim())) return known.trim();
  if (UUID_RE.test(v)) return 'Unknown user';
  return v;
}

/**
 * IDI-540 (IDI-194 slice B): one Acknowledgments panel for a quote, a change
 * order or a job. Rows: date-time, event, document, how, Download. Each row
 * expands to its evidence (signer, IP, signature). This is a dealer-only
 * surface: IP is never shown on a customer-facing page.
 *
 * Row clicks (D-01, IDI-132 slice C): a row's document opens its openTarget.
 *   acceptedConfiguration → that acceptance's own document (the signed PDF
 *     rendered from its AcceptedConfiguration, so a voided — reopened —
 *     acceptance still opens what the customer accepted);
 *   liveDocument → the current send's live, locked Review & Send page;
 *   superseded / migrated rows are not clickable and say why (openReason);
 *   on a job's tab the converted quote's latest send isn't clickable either
 *   ('Converted to this job', IDI-581); its change orders' still open.
 */
const FETCHERS = {
  quote: (id) => quotesAPI.getAcknowledgements(id),
  // IDI-542: a change order's rows, and a job's (its quote's + its COs').
  change_order: (id) => quotesAPI.getChangeOrderAcknowledgements(id),
  job: (id) => quotesAPI.getJobAcknowledgements(id),
};

// IDI-541: the signed PDF for one acceptance row. IDI-770: a change order's
// prints from its acceptance's stored record. Both get the row's own document
// id (on a job's tab, the row's change order — rowDocument).
const SIGNED_PDF_FETCHERS = {
  quote: (quoteId, configId) => api.downloadSignedQuotePDF(quoteId, configId),
  change_order: (coId, configId) => api.downloadSignedChangeOrderPDF(coId, configId),
};

// IDI-132: where a liveDocument row opens.
const LIVE_DOCUMENT_ROUTES = {
  quote: (id) => `/quotes/${id}/review`,
  change_order: (id) => `/change-orders/${id}/review`,
};

// IDI-542: which document a row belongs to. Job rows carry their own
// documentType / documentId; otherwise the panel's document.
function rowDocument(row, documentType, documentId) {
  if (row.documentType && row.documentId) return { type: row.documentType, id: row.documentId };
  return { type: documentType, id: documentId };
}

function openBase64Pdf(b64) {
  const blob = new Blob([Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))], { type: 'application/pdf' });
  const objUrl = URL.createObjectURL(blob);
  const w = window.open(objUrl, '_blank', 'noopener');
  if (!w) {
    // Pop-up blocked: fall back to a download of the same document.
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = 'accepted-quote.pdf';
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
}

function RouterNavigate({ children }) {
  return children(useNavigate());
}

// Renders `children(navigate)`; outside a router, navigate is a full-page load.
function WithNavigate({ children }) {
  const inRouter = useInRouterContext();
  if (inRouter) return <RouterNavigate>{children}</RouterNavigate>;
  return children((to) => window.location.assign(to));
}

function DocumentCell({ row, doc, documentType, documentId, navigate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const target = row.openable ? row.openTarget : null;
  const liveRoute = LIVE_DOCUMENT_ROUTES[documentType];
  const fetchPdf = SIGNED_PDF_FETCHERS[documentType];
  const canOpen = !!target && (
    (target.kind === 'liveDocument' && !!liveRoute) ||
    (target.kind === 'acceptedConfiguration' && !!target.id && !!fetchPdf)
  );
  const onOpen = async () => {
    if (target.kind === 'liveDocument') {
      navigate(liveRoute(documentId));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetchPdf(documentId, target.id);
      const data = res?.data || res;
      if (!data?.pdf_base64) throw new Error('No PDF returned');
      openBase64Pdf(data.pdf_base64);
    } catch {
      setError("Couldn't open the accepted document");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {canOpen ? (
        <button
          type="button"
          onClick={onOpen}
          disabled={busy}
          className="focus-ring text-left hover:underline"
          style={{ color: 'var(--color-action-primary)' }}
          data-testid="ack-open-document"
          title={target.kind === 'liveDocument' ? 'Open the sent document' : 'Open what the customer accepted'}
        >
          {doc}
        </button>
      ) : (
        <div>{doc}</div>
      )}
      {row.openReason && (
        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }} title={row.openReason}>
          {row.openReason}
        </div>
      )}
      {error && <div className="text-xs text-red-600" role="alert">{error}</div>}
    </>
  );
}

function saveBase64Pdf(b64, filename) {
  const blob = new Blob([Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))], { type: 'application/pdf' });
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objUrl);
}

function DownloadCell({ row, documentType, documentId }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fetchPdf = SIGNED_PDF_FETCHERS[documentType];
  if (!row.downloadable || !fetchPdf) {
    const reason = row.downloadReason || 'Download not available';
    return (
      <div className="flex flex-col items-end gap-1">
        <Button variant="secondary" size="sm" disabled title={reason}>Download</Button>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{reason}</span>
      </div>
    );
  }
  const onDownload = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetchPdf(documentId, row.downloadConfigId);
      const data = res?.data || res;
      if (!data?.pdf_base64) throw new Error('No PDF returned');
      saveBase64Pdf(data.pdf_base64, data.filename || 'signed-quote.pdf');
    } catch (err) {
      setError("Couldn't download the signed PDF");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <Button variant="secondary" size="sm" onClick={onDownload} disabled={busy}>Download</Button>
      {error && <span className="text-xs text-red-600" role="alert">{error}</span>}
    </div>
  );
}

function Evidence({ row }) {
  const names = useTenantUserNames();
  const actor = resolveActor(row.actor, names);
  let signature = '—';
  if (row.how === 'esign' && row.signatureImageUrl) {
    signature = <img src={row.signatureImageUrl} alt={`Signature of ${actor || 'signer'}`} className="max-h-16" />;
  } else if (row.how === 'manual' && actor) {
    signature = <span className="italic">{actor}</span>;
  }
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm" data-testid="ack-evidence">
      <dt className="font-medium">{row.how === 'manual' ? 'Recorded by' : 'Signer'}</dt><dd>{actor || '—'}</dd>
      <dt className="font-medium">IP</dt><dd>{row.ip || '—'}</dd>
      <dt className="font-medium">Signature</dt><dd>{signature}</dd>
    </dl>
  );
}

export default function AcknowledgmentsPanel({ documentType = 'quote', documentId, fetchEvents }) {
  const [state, setState] = useState({ status: 'loading', rows: [], error: null });
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    const fetcher = fetchEvents || FETCHERS[documentType];
    if (!documentId || !fetcher) return () => {};
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: null }));
    Promise.resolve()
      .then(() => fetcher(documentId))
      .then((rows) => {
        if (!cancelled) setState({ status: 'ready', rows: deriveAcknowledgmentRows(rows, { context: documentType }), error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', rows: [], error: err });
      });
    return () => { cancelled = true; };
  }, [documentType, documentId, fetchEvents]);

  useEffect(() => load(), [load]);

  const { sortedRows: sortedAckRows, getSortProps } = useTableSort(state.rows, ACK_SORT_COLUMNS);

  if (state.status === 'loading') {
    return <div className="flex items-center justify-center py-8"><Spinner size="md" /></div>;
  }
  if (state.status === 'error') {
    return (
      <ErrorState
        title="Couldn't load acknowledgments"
        message="Something went wrong fetching this document's acknowledgment history."
        onRetry={load}
      />
    );
  }
  if (state.rows.length === 0) {
    return (
      <EmptyState
        title="No acknowledgment events yet"
        description="Sends, acceptances and declines for this document will appear here."
      />
    );
  }

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  return (
    <WithNavigate>{(navigate) => (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader><span className="sr-only">Details</span></TableHeader>
          <TableHeader {...getSortProps('at')}>Date &amp; time</TableHeader>
          <TableHeader {...getSortProps('event')}>Event</TableHeader>
          <TableHeader {...getSortProps('document')}>Document</TableHeader>
          <TableHeader {...getSortProps('how')}>How</TableHeader>
          <TableHeader align="right">Download</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedAckRows.map((row) => {
          const open = !!expanded[row.key];
          // IDI-542: the reader's documentLabel ("<name> · CO-nnn", or the
          // quote number) says which document a row is; else its number.
          const doc = [row.documentLabel || row.documentNumber, row.sendLabel].filter(Boolean).join(' · ') || '—';
          const rowDoc = rowDocument(row, documentType, documentId);
          return (
            <React.Fragment key={row.key}>
              <TableRow data-testid="ack-row" data-openable={row.openable ? 'true' : 'false'}>
                <TableCell>
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={open ? 'Hide evidence' : 'Show evidence'}
                    onClick={() => toggle(row.key)}
                    className="focus-ring px-1"
                  >
                    {open ? '▾' : '▸'}
                  </button>
                </TableCell>
                <TableCell>{formatDateTime(row.at) || '—'}</TableCell>
                <TableCell>{row.eventLabel}</TableCell>
                <TableCell>
                  <DocumentCell row={row} doc={doc} documentType={rowDoc.type} documentId={rowDoc.id} navigate={navigate} />
                </TableCell>
                <TableCell>{row.howLabel}</TableCell>
                <TableCell align="right">
                  {row.showDownload && (
                    <DownloadCell row={row} documentType={rowDoc.type} documentId={rowDoc.id} />
                  )}
                </TableCell>
              </TableRow>
              {open && (
                <TableRow>
                  <TableCell />
                  <TableCell colSpan={5}><Evidence row={row} /></TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
    )}</WithNavigate>
  );
}
